import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Clock, Sparkles, MessageCircle, Headphones, CalendarDays, Phone } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import SlotPicker from '@/components/site/SlotPicker';
import CallbackForm from '@/components/site/CallbackForm';
import AfterSubmitSteps from '@/components/site/AfterSubmitSteps';
import AnalyticsBeacon from '@/components/AnalyticsBeacon';

const COPY = {
  it: {
    eyebrow: 'PRENOTAZIONE CALL · GRATUITA · 30 MIN',
    title: 'Prenota una call con il nostro team',
    subtitle: 'Capiamo insieme se Prime Vision è giusto per il tuo studio. Niente impegni, solo una chiacchierata di 30 minuti.',
    back: 'Torna alla home',
    perks: [
      { icon: Clock, label: 'Solo 30 minuti' },
      { icon: ShieldCheck, label: '100% confidenziale · GDPR' },
      { icon: Sparkles, label: 'Consulente medico dedicato' },
      { icon: Headphones, label: 'Nessuna pressione commerciale' },
    ],
    waCta: 'Scrivici su WhatsApp',
    waSub: 'Risposta in giornata',
    footer: 'Prime Vision · Software gestionale oculistico · primevision.app',
  },
  en: {
    eyebrow: 'BOOK A CALL · FREE · 30 MIN',
    title: 'Book a call with our team',
    subtitle: 'Let\'s see together if Prime Vision is the right fit for your practice. No commitments — just a 30-min chat.',
    back: 'Back to home',
    perks: [
      { icon: Clock, label: 'Only 30 minutes' },
      { icon: ShieldCheck, label: '100% confidential · GDPR' },
      { icon: Sparkles, label: 'Dedicated medical consultant' },
      { icon: Headphones, label: 'No sales pressure' },
    ],
    waCta: 'Message us on WhatsApp',
    waSub: 'Reply same day',
    footer: 'Prime Vision · Ophthalmology practice management · primevision.app',
  },
};

export const BookACallPage = () => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;
  const [mode, setMode] = useState('slot');

  useEffect(() => {
    document.title = locale === 'en'
      ? 'Book a call · Prime Vision'
      : 'Prenota una call · Prime Vision';
  }, [locale]);

  return (
    <main
      className="min-h-screen bg-gradient-to-b from-pv-mist via-white to-pv-mist relative overflow-x-hidden"
      data-testid="pv-bookacall-page"
    >
      {/* decorative blobs */}
      <div className="absolute -top-20 -right-32 w-[520px] h-[520px] rounded-full bg-pv-teal/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-[440px] h-[440px] rounded-full bg-pv-orange/10 blur-3xl pointer-events-none" />

      {/* header */}
      <header className="relative z-10 border-b border-slate-200/60 bg-white/70 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 md:h-18 flex items-center justify-between">
          <Link to="/" data-testid="pv-bookacall-logo" className="inline-flex items-center gap-2">
            <img
              src="/assets/prime-vision-logo.png"
              alt="Prime Vision"
              className="h-8 md:h-9"
            />
          </Link>
          <Link
            to="/"
            data-testid="pv-bookacall-back"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-slate-600 hover:text-pv-teal transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {c.back}
          </Link>
        </div>
      </header>

      {/* main content */}
      <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8 pt-10 pb-16 md:pt-16 md:pb-24">
        {/* hero text */}
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pv-orange/12 border border-pv-orange/40 text-pv-orangeDeep text-[10.5px] font-mono uppercase tracking-[0.22em] font-bold mb-4">
            <Sparkles className="w-3 h-3" />
            {c.eyebrow}
          </div>
          <h1 className="font-heading font-bold text-pv-ink text-[34px] sm:text-[42px] md:text-[54px] leading-[1.05] tracking-normal">
            {c.title}
          </h1>
          <p className="mt-4 text-slate-600 text-[15px] md:text-[17px] leading-relaxed max-w-2xl">
            {c.subtitle}
          </p>
          {/* perks pills */}
          <div className="mt-6 flex flex-wrap gap-2">
            {c.perks.map((p) => (
              <div
                key={p.label}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 text-[12px] font-semibold"
              >
                <p.icon className="w-3.5 h-3.5 text-pv-teal" />
                {p.label}
              </div>
            ))}
          </div>
        </div>

        {/* slot picker / callback */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 mb-5 shadow-[0_4px_14px_-6px_rgba(15,23,42,0.10)]" data-testid="pv-bookcall-mode-switch">
              <button
                type="button"
                onClick={() => setMode('slot')}
                data-testid="pv-bookcall-mode-slot"
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-bold transition-colors ${
                  mode === 'slot' ? 'bg-pv-orange text-white shadow-[0_4px_10px_-4px_rgba(255,107,53,0.55)]' : 'text-slate-600 hover:text-pv-orange'
                }`}
              >
                <CalendarDays className="w-3.5 h-3.5" />
                {locale === 'en' ? 'Pick a slot · Thursday afternoon' : 'Scegli giorno · giovedì pomeriggio'}
              </button>
              <button
                type="button"
                onClick={() => setMode('callback')}
                data-testid="pv-bookcall-mode-callback"
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-bold transition-colors ${
                  mode === 'callback' ? 'bg-pv-teal text-white shadow-[0_4px_10px_-4px_rgba(27,127,184,0.55)]' : 'text-slate-600 hover:text-pv-teal'
                }`}
              >
                <Phone className="w-3.5 h-3.5" />
                {locale === 'en' ? 'We call you back' : 'Ti richiamiamo noi'}
              </button>
            </div>
            {mode === 'slot' ? <SlotPicker /> : <CallbackForm />}
          </div>

          {/* side panel — WhatsApp + reassurance */}
          <aside className="lg:col-span-4 space-y-4">
            {/* WhatsApp big CTA */}
            <div className="bg-gradient-to-br from-[#25D366]/12 via-white to-[#25D366]/6 border border-[#25D366]/40 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#25D366] flex items-center justify-center shadow-[0_8px_18px_-6px_rgba(37,211,102,0.55)] shrink-0">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current" aria-hidden="true">
                    <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 1.75.46 3.46 1.32 4.97L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.44 9.9-9.9a9.82 9.82 0 0 0-2.9-7.03ZM12.05 20.13h-.01a8.22 8.22 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.21 8.21 0 1 1 6.99 3.86Z"/>
                  </svg>
                </div>
                <div>
                  <div className="font-heading font-bold text-pv-ink text-[14px] leading-tight">{c.waCta}</div>
                  <div className="text-[12px] text-slate-600 mt-1">{c.waSub}</div>
                </div>
              </div>
              <a
                href="https://wa.me/393520526856?text=Ciao!%20Vorrei%20fissare%20una%20call%20con%20Prime%20Vision."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pv-bookacall-wa-cta"
                className="pv-btn-whatsapp pv-btn-block mt-4"
              >
                <MessageCircle className="w-4 h-4" />
                +39 352 052 6856
              </a>
            </div>

            {/* Trust signals */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
                {locale === 'en' ? 'WHAT TO EXPECT' : 'COSA ASPETTARSI'}
              </div>
              <ul className="space-y-2.5">
                {(locale === 'en' ? [
                  'Quick chat about your current setup',
                  'Live demo of Prime Vision tailored to you',
                  'Honest Q&A — even on price',
                  'No commitment after the call',
                ] : [
                  'Una breve chiacchierata sul tuo studio',
                  'Demo live di Prime Vision su misura',
                  'Domande aperte — anche sul prezzo',
                  'Nessun impegno dopo la call',
                ]).map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12.5px] text-slate-700">
                    <span className="inline-flex w-4 h-4 rounded-full bg-pv-teal/15 text-pv-teal items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">{i + 1}</span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* footer */}
      <AfterSubmitSteps variant="demo" testId="pv-bookacall-after-steps" />

      <footer className="relative z-10 border-t border-slate-200/60 bg-white/60 backdrop-blur-md py-6">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center text-[11.5px] text-slate-500 font-medium">
          {c.footer}
        </div>
      </footer>

      <AnalyticsBeacon locale={locale} />

      {/* Floating WhatsApp button — also boosts conversion on this page */}
      <a
        href="https://wa.me/393520526856?text=Ciao!%20Vorrei%20fissare%20una%20call%20con%20Prime%20Vision."
        target="_blank"
        rel="noopener noreferrer"
        data-testid="pv-bookacall-wa-float"
        aria-label="Scrivici su WhatsApp"
        className="pv-btn-whatsapp fixed z-[10000] right-2 sm:right-3 bottom-2 sm:bottom-2.5 !pl-3 !pr-4 !py-3 !rounded-full border border-white/20"
      >
        <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-white/20">
          <span className="absolute inset-0 rounded-full bg-white/30 animate-ping opacity-50" />
          <svg viewBox="0 0 24 24" className="relative w-4 h-4 fill-current" aria-hidden="true">
            <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 1.75.46 3.46 1.32 4.97L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.44 9.9-9.9a9.82 9.82 0 0 0-2.9-7.03ZM12.05 20.13h-.01a8.22 8.22 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.21 8.21 0 1 1 6.99 3.86Zm4.51-6.16c-.25-.12-1.47-.72-1.7-.81-.23-.08-.4-.12-.56.12-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23a7.49 7.49 0 0 1-1.38-1.72c-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.07-.1-.23-.16-.48-.29Z"/>
          </svg>
        </span>
        <span className="hidden sm:inline">{locale === 'en' ? 'Message us on WhatsApp' : 'Scrivici su WhatsApp'}</span>
        <span className="sm:hidden">{locale === 'en' ? 'WhatsApp' : 'Scrivici'}</span>
      </a>
    </main>
  );
};

export default BookACallPage;
